'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { normalizeResponsiveStyles } from '../../utils/normalize-responsive-styles';
import { Slot } from '../slot';
import { ToggleButtonGroupContext } from './context/toggle-button-group.context';
import { toggleButtonGroupRecipe } from './styles/toggle-button-group.styles';
import type { ToggleButtonGroupPropsInterface } from './types/toggle-button-group.types';

/**
 * Be sure to add an appropriate
 * [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) for assistive technologies.
 *
 * @related ['ToggleButton', 'LoadingButton', 'TextButton', 'Button']
 * @since 8.0.0
 */

export const ToggleButtonGroup = forwardRef<HTMLDivElement, ToggleButtonGroupPropsInterface>(
  (props: ToggleButtonGroupPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      conjoined = true,
      children,
      className,
      css: cssProp,
      fluid = false,
      reverse = false,
      orientation = 'horizontal',
      value,
      defaultValue,
      onValueChange,
      size = 'sm',
      disabled,
      ...rest
    } = props;

    const isValueControlled = 'value' in props;

    const [valueState, setValueState] = useControllableValue<string | Array<string> | undefined>({
      controlledValue: value,
      uncontrolledValue: defaultValue,
      isControlled: isValueControlled,
      isUncontrolled: 'defaultValue' in props,
      onChange: (newValue) => {
        if (newValue !== undefined) {
          onValueChange?.(newValue);
        }
      },
      componentName: 'ToggleButtonGroup',
      controlledPropName: 'value',
      uncontrolledPropName: 'defaultValue',
    });

    const reverseForStyles = normalizeResponsiveStyles(reverse, orientation);
    const conjoinedForStyles = normalizeResponsiveStyles(conjoined, orientation);
    const responsiveFluid = splitResponsiveVariant('fluid', fluid);
    const responsiveReverse = splitResponsiveVariant('reverse', reverseForStyles);
    const responsiveOrientation = splitResponsiveVariant('orientation', orientation);
    const responsiveConjoined = splitResponsiveVariant('conjoined', conjoinedForStyles);

    const styles = css(
      toggleButtonGroupRecipe.raw({
        ...responsiveConjoined,
        ...responsiveFluid,
        ...responsiveReverse,
        ...responsiveOrientation,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(
      () => ({ size, value: valueState, setValue: setValueState, disabled, fluid }),
      [size, valueState, setValueState, disabled, fluid],
    );

    return (
      <ToggleButtonGroupContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="ToggleButtonGroup"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </Component>
      </ToggleButtonGroupContext.Provider>
    );
  },
);

ToggleButtonGroup.displayName = 'ToggleButtonGroup';
